import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Icon from '../components/Icon';
import { F1_DRIVERS, driverImgUrl } from '../constants/f1Drivers';
import { F1_CONSTRUCTORS } from '../constants/f1Constructors';

const DRIVER_POINTS  = 100;
const CONSTR_POINTS  = 50;
const BONUS_POINTS   = 30;

const DriverCard = ({ driver, selected, onSelect, disabled }) => {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <button type="button" onClick={() => !disabled && onSelect(selected ? null : driver.name)}
      className={`relative flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all ${
        disabled ? 'opacity-60 cursor-default' : 'cursor-pointer'
      } ${selected ? 'scale-[1.04]' : 'border-zinc-800 hover:border-zinc-600'}`}
      style={selected ? { borderColor: driver.color, boxShadow: `0 0 14px ${driver.color}44` } : {}}>
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: driver.color }}>
          <Icon name="check" size={9} className="text-white" />
        </div>
      )}
      <div className="w-12 h-12 rounded-full overflow-hidden bg-zinc-800 border" style={{ borderColor: `${driver.color}44` }}>
        {!imgFailed
          ? <img src={driverImgUrl(driver.img)} alt={driver.name} className="w-full h-full object-cover object-top" onError={() => setImgFailed(true)} />
          : <span className="w-full h-full flex items-center justify-center text-xs font-black" style={{ color: driver.color }}>{driver.name.split(' ').map(n => n[0]).join('')}</span>
        }
      </div>
      <p className="text-white text-[10px] font-bold leading-tight text-center">{driver.name.split(' ').at(-1)}</p>
      <p className="text-[9px] font-medium" style={{ color: driver.color }}>{driver.number}</p>
    </button>
  );
};

const ConstructorCard = ({ constructor: c, selected, onSelect, disabled }) => (
  <button type="button" onClick={() => !disabled && onSelect(selected ? null : c.name)}
    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all text-left w-full ${
      disabled ? 'opacity-60 cursor-default' : 'cursor-pointer'
    } ${selected ? 'scale-[1.01]' : 'border-zinc-800 hover:border-zinc-600'}`}
    style={selected ? { borderColor: c.color, backgroundColor: `${c.color}11`, boxShadow: `0 0 10px ${c.color}33` } : {}}>
    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
    <span className={`text-sm font-bold ${selected ? 'text-white' : 'text-zinc-300'}`}>{c.name}</span>
    {selected && <Icon name="check" size={14} className="ml-auto shrink-0" style={{ color: c.color }} />}
  </button>
);

const SeasonChampion = () => {
  const { user } = useAuth();
  const [season, setSeason]       = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [driverPick, setDriverPick]   = useState(null);
  const [constructorPick, setConstructorPick] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    const fetches = [
      api.get('/season'),
      api.get('/season/leaderboard'),
      user ? api.get('/season/my-prediction') : Promise.resolve({ data: null }),
    ];
    Promise.all(fetches)
      .then(([sRes, lbRes, predRes]) => {
        setSeason(sRes.data);
        setLeaderboard(lbRes.data.predictions || []);
        if (predRes.data) {
          setPrediction(predRes.data);
          setDriverPick(predRes.data.driverPick || null);
          setConstructorPick(predRes.data.constructorPick || null);
        }
      })
      .finally(() => setLoading(false));
  }, [user]);

  const now = new Date();
  const deadline = season?.deadline ? new Date(season.deadline) : null;
  const resultsPublished = season?.resultsPublished;
  const isLocked = (!!deadline && now > deadline) || !!resultsPublished;

  const alreadySubmitted = !!prediction;
  const hasChanges = !alreadySubmitted && !!(driverPick || constructorPick);

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const res = await api.post('/season/my-prediction', { driverPick, constructorPick });
      setPrediction(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-zinc-400">Chargement...</div>
  );

  const driverObj = F1_DRIVERS.find(d => d.name === driverPick);
  const constructorObj = F1_CONSTRUCTORS.find(c => c.name === constructorPick);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      {/* Header */}
      <div className="mb-8">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Saison {season?.year}</p>
        <h1 className="text-3xl font-black text-white">Champion de saison</h1>
        <p className="text-zinc-400 mt-1 text-sm">Pronostique le champion pilote et constructeur avant la clôture.</p>

        {deadline && !isLocked && (
          <div className="mt-3 inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2">
            <Icon name="clock" size={14} className="text-zinc-400" />
            <span className="text-xs text-zinc-400">
              Fermeture le <span className="text-white font-semibold">
                {deadline.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </span>
          </div>
        )}

        {isLocked && !resultsPublished && (
          <div className="mt-3 inline-flex items-center gap-2 bg-orange-900/20 border border-orange-800 rounded-lg px-3 py-2">
            <Icon name="lock" size={14} className="text-orange-400" />
            <span className="text-xs text-orange-400 font-medium">Prédictions verrouillées — résultats en attente de fin de saison</span>
          </div>
        )}

        {resultsPublished && (
          <div className="mt-3 inline-flex items-center gap-2 bg-green-900/20 border border-green-800 rounded-lg px-3 py-2">
            <Icon name="trophy" size={14} className="text-yellow-400" />
            <span className="text-xs text-green-400 font-medium">Résultats publiés !</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Left: pickers */}
        <div className="flex flex-col gap-6">
          {/* Scoring info */}
          <div className="card">
            <h2 className="font-bold text-white mb-3 text-sm flex items-center gap-2">
              <Icon name="star" size={14} className="text-yellow-400" /> Barème des points
            </h2>
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Champion pilote</span>
                <span className="text-white font-bold">{DRIVER_POINTS} pts</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Champion constructeur</span>
                <span className="text-white font-bold">{CONSTR_POINTS} pts</span>
              </div>
              <div className="flex justify-between text-sm border-t border-zinc-800 pt-1.5 mt-0.5">
                <span className="text-zinc-400">Bonus double réussite</span>
                <span className="text-yellow-400 font-bold">+{BONUS_POINTS} pts</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500 text-xs">Maximum possible</span>
                <span className="text-zinc-300 font-bold text-xs">{DRIVER_POINTS + CONSTR_POINTS + BONUS_POINTS} pts</span>
              </div>
            </div>
          </div>

          {/* Results card if published */}
          {resultsPublished && (
            <div className="card border-yellow-800/40 bg-yellow-900/10">
              <h2 className="font-bold text-white mb-3 flex items-center gap-2">
                <Icon name="trophy" size={16} className="text-yellow-400" /> Champions {season.year}
              </h2>
              <div className="flex flex-col gap-2">
                {season.driverChampion && (
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Pilote</span>
                    <span className="text-white font-bold text-sm">{season.driverChampion}</span>
                  </div>
                )}
                {season.constructorChampion && (
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">Constructeur</span>
                    <span className="text-white font-bold text-sm">{season.constructorChampion}</span>
                  </div>
                )}
              </div>
              {prediction?.scored && (
                <div className="mt-3 pt-3 border-t border-yellow-800/40 flex items-center justify-between">
                  <span className="text-zinc-400 text-sm">Ton score</span>
                  <span className={`text-xl font-black ${prediction.score > 0 ? 'text-yellow-400' : 'text-zinc-500'}`}>
                    {prediction.score} pts
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Driver picker */}
          {!user ? (
            <div className="card text-center py-6">
              <p className="text-zinc-400 text-sm mb-3">Connecte-toi pour faire ta prédiction.</p>
              <Link to="/login" className="btn-primary inline-block">Se connecter</Link>
            </div>
          ) : (
            <>
              <div className="card flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-white flex items-center gap-2">
                    <Icon name="user" size={16} className="text-f1red" /> Champion Pilote
                  </h2>
                  {(isLocked || alreadySubmitted) && <span className="text-xs text-orange-400 flex items-center gap-1"><Icon name="lock" size={11} />Verrouillé</span>}
                </div>

                {driverPick && driverObj && (
                  <div className="flex items-center gap-3 rounded-xl px-3 py-2 border" style={{ borderColor: `${driverObj.color}44`, backgroundColor: `${driverObj.color}11` }}>
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-zinc-800 border-2" style={{ borderColor: driverObj.color }}>
                      <img src={driverImgUrl(driverObj.img)} alt={driverObj.name} className="w-full h-full object-cover object-top" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-bold">{driverObj.name}</p>
                      <p className="text-xs font-medium" style={{ color: driverObj.color }}>{driverObj.team}</p>
                    </div>
                    {!isLocked && (
                      <button onClick={() => setDriverPick(null)} className="text-zinc-500 hover:text-zinc-300 shrink-0">
                        <Icon name="x" size={14} />
                      </button>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-4 gap-1.5 max-h-64 overflow-y-auto pr-0.5">
                  {F1_DRIVERS.map(d => (
                    <DriverCard key={d.number} driver={d}
                      selected={driverPick === d.name}
                      onSelect={setDriverPick}
                      disabled={isLocked || alreadySubmitted} />
                  ))}
                </div>
              </div>

              {/* Constructor picker */}
              <div className="card flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-white flex items-center gap-2">
                    <Icon name="flag" size={16} className="text-zinc-400" /> Champion Constructeur
                  </h2>
                  {(isLocked || alreadySubmitted) && <span className="text-xs text-orange-400 flex items-center gap-1"><Icon name="lock" size={11} />Verrouillé</span>}
                </div>

                <div className="flex flex-col gap-1.5">
                  {F1_CONSTRUCTORS.map(c => (
                    <ConstructorCard key={c.name} constructor={c}
                      selected={constructorPick === c.name}
                      onSelect={setConstructorPick}
                      disabled={isLocked || alreadySubmitted} />
                  ))}
                </div>
              </div>

              {/* Save / status */}
              {alreadySubmitted ? (
                <div className="flex items-center gap-2 rounded-xl px-4 py-3 bg-green-900/20 border border-green-800">
                  <Icon name="check" size={14} className="text-green-400 shrink-0" />
                  <p className="text-green-400 text-sm">
                    Prédiction soumise le <span className="font-semibold">{new Date(prediction.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span> — non modifiable.
                  </p>
                </div>
              ) : !isLocked ? (
                <div className="flex flex-col gap-2">
                  {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
                  <p className="text-xs text-zinc-500 text-center">⚠️ Attention : ta prédiction sera définitive et ne pourra pas être modifiée.</p>
                  <button onClick={handleSave} disabled={saving || !hasChanges}
                    className={`btn-primary w-full py-3 text-base ${saved ? 'bg-green-600 hover:bg-green-700' : ''}`}>
                    {saved ? '✓ Soumis !' : saving ? 'Soumission...' : 'Soumettre ma prédiction'}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Right: leaderboard */}
        <div className="card">
          <h2 className="font-bold text-white mb-4 flex items-center gap-2">
            <Icon name="chart" size={16} className="text-f1red" />
            Prédictions ({leaderboard.length})
          </h2>

          {leaderboard.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">Aucune prédiction pour l'instant.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {leaderboard.map((p, i) => {
                const dObj = F1_DRIVERS.find(d => d.name === p.driverPick);
                const cObj = F1_CONSTRUCTORS.find(c => c.name === p.constructorPick);
                const isMe = user && p.user?._id === user.id;
                return (
                  <div key={p._id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors ${isMe ? 'border-f1red/40 bg-f1red/5' : 'border-zinc-800 bg-zinc-800/30'}`}>
                    <span className={`text-xs font-black w-5 text-center ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-amber-600' : 'text-zinc-600'}`}>
                      {resultsPublished ? i + 1 : '—'}
                    </span>
                    {p.user?.avatar
                      ? <img src={p.user.avatar} alt="" className="w-7 h-7 rounded-full object-cover border border-zinc-700" />
                      : <span className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-black text-zinc-300">{p.user?.username?.[0]?.toUpperCase()}</span>
                    }
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${isMe ? 'text-f1red' : 'text-white'} truncate`}>{p.user?.username}{isMe && ' (toi)'}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {dObj && <span className="text-[10px] font-medium" style={{ color: dObj.color }}>{dObj.name.split(' ').at(-1)}</span>}
                        {cObj && <span className="text-[10px] text-zinc-500">· {cObj.name}</span>}
                        {!p.driverPick && !p.constructorPick && <span className="text-[10px] text-zinc-600">Aucun pick</span>}
                      </div>
                    </div>
                    {resultsPublished && (
                      <span className={`text-sm font-black shrink-0 ${p.score > 0 ? 'text-yellow-400' : 'text-zinc-600'}`}>
                        {p.score} pts
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeasonChampion;
